import React, { useRef, useState } from 'react';
import api from '../../utils/api';
import { prepareFileForUpload } from '../../utils/imageUploadCompress';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_PRIORITIES,
  FEEDBACK_SUBMIT_TO,
  validateFeedbackFile,
} from '../../data/feedbackConstants';
import { CheckCircle2, Paperclip, ImageIcon, Loader2 } from 'lucide-react';
import { isMobileContainer } from '../../utils/device';
import { getFeedbackAppVersion, getFeedbackClient, getFeedbackPageUrl } from '../../utils/feedbackSubmitMeta';

const emptyForm = () => ({
  title: '',
  description: '',
  submit_to: '',
  category: 'other',
  priority: 'medium',
});

export default function FeedbackSubmitModal({ open, onOpenChange }) {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [successCode, setSuccessCode] = useState('');
  const screenshotRef = useRef(null);
  const attachmentRef = useRef(null);
  const [screenshotName, setScreenshotName] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const isMobile = isMobileContainer();

  const resetForm = () => {
    setForm(emptyForm());
    setSubmitting(false);
    setSuccessCode('');
    setScreenshotName('');
    setAttachmentName('');
    if (screenshotRef.current) screenshotRef.current.value = '';
    if (attachmentRef.current) attachmentRef.current.value = '';
  };

  const handleOpenChange = (next) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleFilePick = (e, type) => {
    const file = e.target.files?.[0];
    const err = validateFeedbackFile(file);
    if (err) {
      toast({ title: 'Invalid file', description: err, variant: 'destructive' });
      e.target.value = '';
      if (type === 'screenshot') setScreenshotName('');
      else setAttachmentName('');
      return;
    }
    if (type === 'screenshot') setScreenshotName(file?.name || '');
    else setAttachmentName(file?.name || '');
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Title required', description: 'Please enter a feedback title.', variant: 'destructive' });
      return;
    }
    if (!form.description.trim()) {
      toast({ title: 'Description required', description: 'Please describe your feedback.', variant: 'destructive' });
      return;
    }
    if (!form.submit_to) {
      toast({ title: 'Submit to required', description: 'Please choose whether to send feedback to School or Software.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('submit_to', form.submit_to);
      fd.append('category', form.category);
      fd.append('priority', form.priority);
      fd.append('page_url', getFeedbackPageUrl());
      fd.append('app_version', await getFeedbackAppVersion());
      fd.append('client', getFeedbackClient());

      const screenshot = screenshotRef.current?.files?.[0];
      const attachment = attachmentRef.current?.files?.[0];
      if (screenshot) {
        fd.append('screenshot', await prepareFileForUpload(screenshot));
      }
      if (attachment) {
        fd.append('attachment', await prepareFileForUpload(attachment));
      }

      const res = await api.post('/feedback', fd);
      const code = res.data?.data?.feedback_code || '';
      setSuccessCode(code);
    } catch (e) {
      setSubmitting(false);
      toast({
        title: 'Submission failed',
        description: e.response?.data?.message || 'Could not submit feedback. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {successCode ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-6 w-6" />
                Thank you!
              </DialogTitle>
              <DialogDescription className="text-base text-foreground pt-2">
                Your feedback has been submitted successfully.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-border bg-muted/40 p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">Your Feedback ID</p>
              <p className="text-xl font-bold font-mono text-foreground">{successCode}</p>
            </div>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send Feedback</DialogTitle>
              <DialogDescription>
                Help us improve School Analytics. Your page and device info are captured automatically.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="fb-title">Feedback Title *</Label>
                <Input
                  id="fb-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Brief summary of your feedback"
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fb-desc">Feedback Description *</Label>
                <Textarea
                  id="fb-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the issue or suggestion in detail..."
                  rows={4}
                  maxLength={5000}
                />
              </div>

              <div className="space-y-2">
                <Label>Submit to *</Label>
                <Select value={form.submit_to || undefined} onValueChange={(v) => setForm((f) => ({ ...f, submit_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select recipient" /></SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_SUBMIT_TO.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  School = your school administration; Software = platform support team
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FEEDBACK_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FEEDBACK_PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Screenshot (optional)</Label>
                  <input
                    ref={screenshotRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    capture={isMobile ? 'environment' : undefined}
                    className="hidden"
                    onChange={(e) => handleFilePick(e, 'screenshot')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => screenshotRef.current?.click()}
                  >
                    <ImageIcon className="h-4 w-4 mr-2 shrink-0" />
                    <span className="truncate">{screenshotName || 'Choose screenshot'}</span>
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>File Attachment (optional)</Label>
                  <input
                    ref={attachmentRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                    className="hidden"
                    onChange={(e) => handleFilePick(e, 'attachment')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => attachmentRef.current?.click()}
                  >
                    <Paperclip className="h-4 w-4 mr-2 shrink-0" />
                    <span className="truncate">{attachmentName || 'Choose file'}</span>
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting…
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
